import { Box, Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { SelectInput } from "components/FormControls/Select/Select";
import { OLSwitch } from "components/FormControls/Toggle";
import { WalletTypeAutocompleteEditMode } from "components/Layout/Campaign/Common/Wallets/WalletTypeAutocompleteEditMode";
import { campaignEffects } from "components/Layout/Campaign/Common/campaignHelpers";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  CampaignEffect,
  CampaignEffectType,
  CampaignTriggers,
  CampaignTypes,
  CampaignTypeUnion,
  SelectedSchemaCondition,
} from "types/campaigns";
import { markEditResolved } from "utilities/checkRulesEditMode";
import { EffectReadOnlyWrapper } from "./EffectReadOnlyWrapper";
import { EffectEvent } from "./EffectSections/EffectEvent";
import { EffectInternal } from "./EffectSections/EffectInternal";
import { EffectTransaction } from "./EffectSections/EffectTransaction";
import { ExpirationSettings } from "./EffectSections/ExpirationSettings/ExpirationSettings";
import { PendingSettings } from "./EffectSections/PendingSettings/PendingSettings";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    effectWrapper: {
      background: theme.gray50,
      border: `1px solid ${theme.gray200}`,
      borderLeft: "none",
      borderRight: "none",
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(6, 8),
      position: "relative",
      width: "100%",
      "& + &": {
        marginTop: theme.spacing(8),
      },
      "&:last-child": {
        borderBottom: "none",
        borderRadius: "0 0 8px 8px",
      },
    },
    condition: {
      marginTop: theme.spacing(12),
    },
    autocomplete: {
      background: theme.white50,
    },
    saveButton: { padding: theme.spacing(4, 11) },
    clearIcon: {
      position: "absolute",
      top: "8px",
      right: "8px",
    },
    input: {
      backgroundColor: theme.white50,
    },
    formControl: {
      width: "100%",
    },
    buttonWrapper: {
      marginTop: theme.spacing(12),
      display: "flex",
      justifyContent: "flex-end",

      "& button": {
        marginLeft: theme.spacing(8),
      },
    },
    toggle: {
      color: theme.primary800,
      marginBottom: theme.spacing(12),
    },
    margin: {
      margin: theme.spacing(6, 0),
    },
    advancedSettingsInfo: {
      color: theme.gray500,
      fontSize: 12,
      marginTop: theme.spacing(6),
      whiteSpace: "break-spaces",
    },
    divider: {
      height: "1px",
      backgroundColor: theme.gray200,
      marginTop: theme.spacing(6),
    },
    walletSettingsWrapper: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: theme.spacing(6),
    },
    walletSettingsLabel: {
      fontSize: "14px",
      lineHeight: "24px",
      color: theme.gray500,
    },
    walletSettingsValue: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "18px",
      color: theme.gray800,
    },
    description: {
      color: theme.gray500,
      fontSize: 12,
      whiteSpace: "break-spaces",
    },
  }),
);

export type EffectError = {
  pointsRule?: string[];
  rewardId?: string[];
  customAttributeKey?: string[];
  couponValueRule?: string[];
  customAttributeValueRule?: string[];
  walletCode?: string[];
};

interface Props {
  input: {
    effect: CampaignEffectType;
  };
  index: number;
  replace: (index: number, selected: any) => void;
  trigger: CampaignTriggers;
  ruleIndex: number;
  selectedSchemaConditions?: SelectedSchemaCondition[];
  effect: CampaignEffect;
  setFieldValue: (fieldName: string, value: any) => void;
  remove: (index: number) => void;
  insert: (index: number, value: any) => void;
  setFieldError: (field: string, errorMsg?: any) => void;
  error: EffectError;
  type: CampaignTypeUnion;
}

export const Effect = ({
  input,
  replace,
  insert,
  remove,
  effect,
  index,
  trigger,
  ruleIndex,
  selectedSchemaConditions,
  setFieldValue,
  setFieldError,
  error,
  type,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const { isEditing, isSaved } = effect;
  const fieldKey = isEditing
    ? `rules.[${ruleIndex}].effects.[${index}].draft`
    : `rules.[${ruleIndex}].effects.[${index}]`;
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const { useWalletTypeList, useWalletTypeData } = useWallets();

  const { data: selectedWalletTypeListItem } = useWalletTypeList({
    filters: [
      {
        condition: "eq",
        query: "code",
        value: effect?.draft?.walletCode || defaultWallet.code,
      },
    ],
  });
  const selectedWalletTypeListItemId =
    selectedWalletTypeListItem?.items[0]?.walletTypeId;

  const { data: selectedWalletTypeDetails } = useWalletTypeData(
    selectedWalletTypeListItemId as string,
  );
  const unitDaysExpiryAfter = selectedWalletTypeDetails?.unitDaysExpiryAfter;
  const unitDaysActiveCount = selectedWalletTypeDetails?.unitDaysActiveCount;

  const prepareUnitExpirationMethod = () => {
    switch (unitDaysExpiryAfter) {
      case "all_time_active":
        return message({ id: "noExpiration" });
      case "after_x_days":
        return message({ id: "afterXDays" });
      case "at_the_end_of_the_month":
        return message({ id: "atTheEndOfTheMonth" });
      case "at_the_end_of_the_x_th_year":
        return message({ id: "atTheEndOfTheXthYear" });
      case "at_the_end_of_the_chosen_date":
        return message({ id: "annualExpirationOnChosenDate" });
      default:
        return "-";
    }
  };

  const prepareUnitExpirationPeriod = () => {
    switch (unitDaysExpiryAfter) {
      case "all_time_active":
      case "at_the_end_of_the_month":
        return "-";
      case "after_x_days":
        return `${unitDaysActiveCount} ${message({ id: "day(s)" }).toLowerCase()}`;
      case "at_the_end_of_the_x_th_year":
        const unitYearsActiveCount =
          selectedWalletTypeDetails?.unitYearsActiveCount;
        if (unitYearsActiveCount === 0) {
          return message({ id: "atTheEndOfThisYear" });
        }

        return message(
          { id: "atTheEndOfNYear" },
          {
            n: unitYearsActiveCount,
          },
        );
      case "at_the_end_of_the_chosen_date":
        return selectedWalletTypeDetails?.unitExpiryDate;
      default:
        return "-";
    }
  };

  const walletSettings = [
    {
      label: message({ id: "unitExpirationMethod" }),
      value: prepareUnitExpirationMethod(),
    },
    {
      label: message({ id: "unitExpirationPeriod" }),
      value: prepareUnitExpirationPeriod(),
    },
    {
      label: message({ id: "unitPendingPeriod" }),
      value: selectedWalletTypeDetails
        ? selectedWalletTypeDetails?.allTimeNotLocked
          ? message({ id: "noPending" })
          : `${selectedWalletTypeDetails?.unitDaysLocked} ${message({ id: "day(s)" })}`
        : "-",
    },
  ];

  const {
    TIME,
    CUSTOM_EVENT,
    INTERNAL_EVENT,
    TRANSACTION,
    RETURN_TRANSACTION,
    CUSTOM_EVENT_UNIQUE_CODE,
    ACHIEVEMENT,
  } = CampaignTriggers;
  const isReferralCampaign = type === CampaignTypes.REFERRAL;
  const isTimeBasedCampaign = trigger === CampaignTriggers.TIME;
  const campaignTypeName = message({
    id: isReferralCampaign
      ? "referral"
      : isTimeBasedCampaign
        ? "automation"
        : "campaign",
  }).toLowerCase();

  return (
    <>
      {isEditing ? (
        <section
          className={classes.effectWrapper}
          data-test-id={`effect-${index}-container`}
          data-attr-edit={`effect-${ruleIndex}-${index}`}
        >
          <Typography>
            {isSaved
              ? message({ id: "editEffect" })
              : message({ id: "newEffect" })}
          </Typography>
          <Grid container className={classes.condition} spacing={12}>
            <Grid item xs={6}>
              <SelectInput
                name={`${fieldKey}.effect`}
                items={campaignEffects.map(({ effect }) => ({
                  id: effect,
                  label: message({ id: effect }),
                }))}
                data-test-id="effect-type"
                label={message({ id: "actionType" })}
              />
            </Grid>

            {["give_points", "deduct_unit"].includes(input.effect!) && (
              <Grid item xs={6}>
                <WalletTypeAutocompleteEditMode
                  fieldName={fieldKey}
                  walletCode={
                    isEditing ? effect.draft?.walletCode : effect.walletCode
                  }
                />
              </Grid>
            )}

            {input.effect &&
              ([INTERNAL_EVENT, TIME, ACHIEVEMENT].includes(trigger) ? (
                <EffectInternal effect={input.effect} fieldName={fieldKey} />
              ) : [TRANSACTION, RETURN_TRANSACTION].includes(trigger) ? (
                <EffectTransaction
                  effect={input.effect}
                  fieldName={fieldKey}
                  isReferralCampaign={isReferralCampaign}
                />
              ) : trigger === CUSTOM_EVENT ||
                trigger === CUSTOM_EVENT_UNIQUE_CODE ? (
                <EffectEvent
                  selectedSchemaConditions={selectedSchemaConditions || []}
                  editMode={true}
                  effect={input.effect}
                  fieldName={fieldKey}
                />
              ) : null)}

            {input.effect === "give_points" && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <OLSwitch
                    checked={effect.draft?.isChecked}
                    data-test-id="expiration-rules-switch"
                    onChange={(event) => {
                      setFieldValue(
                        `${fieldKey}.isChecked`,
                        event.target.checked,
                      );
                      setFieldValue(
                        `${fieldKey}.unitsExpirationRule.expirationStrategy`,
                        event.target.checked ? "expression" : undefined,
                      );
                      setFieldValue(
                        `${fieldKey}.unitsLockRule.lockStrategy`,
                        event.target.checked ? "expression" : undefined,
                      );
                      if (!event.target.checked) {
                        setFieldValue(
                          `${fieldKey}.unitsExpirationRule.expression`,
                          undefined,
                        );
                        setFieldValue(
                          `${fieldKey}.unitsLockRule.expression`,
                          undefined,
                        );
                      }
                    }}
                  />
                  <Typography>
                    {message({ id: "overrideUnitsSettings" })}
                  </Typography>
                </Box>

                {effect.draft?.isChecked ? (
                  <>
                    <div className={classes.margin}>
                      <Typography>
                        {message({ id: "expirationSettings" })}
                      </Typography>
                      <Typography className={classes.description}>
                        {message(
                          { id: "switchedOnOverrideUnitsInfo" },
                          {
                            type: campaignTypeName,
                          },
                        )}
                      </Typography>
                    </div>
                    <ExpirationSettings
                      trigger={trigger}
                      fieldName={fieldKey}
                      selectedSchemaConditions={selectedSchemaConditions || []}
                      expirationStrategy={
                        effect.draft?.unitsExpirationRule?.expirationStrategy
                      }
                    />
                    <div className={classes.margin}>
                      <Typography>
                        {message({ id: "pendingSettings" })}
                      </Typography>
                      <Typography className={classes.description}>
                        {message(
                          { id: "switchedOnOverridePendingInfo" },
                          {
                            type: campaignTypeName,
                          },
                        )}
                      </Typography>
                    </div>
                    <PendingSettings
                      trigger={trigger}
                      fieldName={fieldKey}
                      selectedSchemaConditions={selectedSchemaConditions || []}
                      lockStrategy={effect.draft?.unitsLockRule?.lockStrategy}
                    />
                  </>
                ) : (
                  <>
                    <Typography className={classes.advancedSettingsInfo}>
                      {message(
                        { id: "switchedOffOverrideUnitsInfo" },
                        {
                          type: campaignTypeName,
                        },
                      )}
                    </Typography>
                    <Box className={classes.divider}></Box>
                    {walletSettings.map((item) => (
                      <Box
                        className={classes.walletSettingsWrapper}
                        key={item.label}
                      >
                        <Typography className={classes.walletSettingsLabel}>
                          {item.label}
                        </Typography>
                        <Typography className={classes.walletSettingsValue}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Grid>
            )}
          </Grid>
          <div className={classes.buttonWrapper}>
            <OLButton
              data-test-id="effect-cancel"
              onClick={() => {
                markEditResolved(`effect-${ruleIndex}-${index}`);
                if (isSaved) {
                  setFieldValue(`rules.[${ruleIndex}].effects.[${index}]`, {
                    ...effect,
                    isEditing: false,
                  });
                } else {
                  remove(index);
                }
              }}
              size="small"
            >
              {isSaved ? message({ id: "close" }) : message({ id: "cancel" })}
            </OLButton>
            <OLButton
              className={classes.saveButton}
              data-test-id="effect-save"
              onClick={() => {
                // Save value and remove draft key
                markEditResolved(`effect-${ruleIndex}-${index}`);
                setFieldValue(`rules.[${ruleIndex}].effects.[${index}]`, {
                  ...effect.draft,
                  isEditing: false,
                  isSaved: true,
                  unitsExpirationRule: !effect.draft?.isChecked
                    ? undefined
                    : effect.draft?.unitsExpirationRule,

                  isChecked: effect.draft?.isChecked,
                });

                setFieldError(
                  `rules.[${ruleIndex}].effects.[${index}]`,
                  undefined,
                );
              }}
              variant="contained"
              size="small"
            >
              {message({ id: "saveEffect" })}
            </OLButton>
          </div>
        </section>
      ) : (
        <EffectReadOnlyWrapper
          remove={() => remove(index)}
          edit={() => {
            setFieldError(
              `rules.[${ruleIndex}].effects.[${index}].draft`,
              error,
            );
            setFieldValue(fieldKey, {
              ...effect,
              draft: { ...effect },
              isEditing: true,
              isSaved: true,
            });
          }}
          duplicate={() =>
            insert(index + 1, {
              draft: { ...effect },
              isEditing: true,
              isSaved: false,
            })
          }
          index={index}
          effect={effect}
          error={error}
        />
      )}
    </>
  );
};
